/**
 * La ficha del jugador: lo que ha hecho, lo que vale y lo que le falta.
 *
 * Es la pantalla que da sentido a jugar un partido mas, asi que esta construida
 * como la ficha de un juego de deportes y no como una tabla de contadores:
 *
 * - **FICHA**: una media grande, un hexagono de atributos y una barra de nivel.
 *   Un numero que sube es un motivo; una lista de cifras no lo es.
 * - **HISTORIAL**: cada partido con su ficha entera, desplegable.
 * - **LOGROS**: insignias de cuatro escalones (siempre hay una a medias) y
 *   trofeos de momento (siempre hay uno que contar).
 * - **RÉCORDS**: tu mejor version, y la curva de la media a lo largo de la
 *   carrera.
 *
 * Las cuatro pestanas se montan de una vez y se enseñan escondiendo: cambiar de
 * pestana no puede costar un reflow de toda la pantalla en un movil.
 */
import "./career.css";
import { lang, t } from "../i18n/index.js";
import { mountGoogleButton } from "./googleSignIn.js";
import { mountPlayerCard } from "./playerCard.js";
import {
  allBadgeProgress,
  badgeTierTotal,
  RARITY_NAMES,
  TIER_NAMES,
  TROPHIES,
  type BadgeProgress,
  type Rarity,
  type Trophy,
} from "../game/badges.js";
import type { ApplyResult, MatchRecord, Profile, ProfileSide } from "../game/profile.js";
import type { FriendBoardRow } from "@golazo/shared";
import { formOf, levelFor, ratePlayer, type PlayerRating } from "../game/rating.js";

export interface AccountBinding {
  /** Pide al server un codigo de recuperacion. */
  requestCode(): void;
  /** Canjea un codigo sacado en otro dispositivo. */
  redeem(code: string): void;
  /** Manda a verificar el ID token de Google. */
  google(credential: string): void;
}

/** Los dos perfiles que se enseñan, cada uno en su pestaña. */
export interface ProfilePair {
  online: Profile;
  offline: Profile;
}

/** Lo social de las estadisticas online: la tabla entre amigos y sus fichas. */
export interface CareerSocial {
  board(): Promise<FriendBoardRow[] | null>;
  /** Abre la ficha de un amigo comparada con la tuya. */
  open(clientId: string, nickname: string): void;
}

export interface CareerScreenHandle {
  /** Engancha la parte social. Sin esto no hay pestaña de amigos. */
  setSocial(social: CareerSocial): void;
  show(profiles: ProfilePair): Promise<void>;
  hide(): void;
  /** Cerrar como si se pulsara "volver" (resuelve `show`). */
  close(): void;
  /** Conecta la tarjeta de cuenta con el server. Sin esto la tarjeta no se ve. */
  bindAccount(binding: AccountBinding): void;
  /** El server ha emitido un codigo. Se enseña una sola vez. */
  showCode(code: string): void;
  /** Resultado de un canje. */
  showRedeemResult(ok: boolean, message: string): void;
  /**
   * Configura Google. `clientId` null = el server no lo tiene puesto, y
   * entonces no se enseña nada.
   */
  setGoogle(clientId: string | null, linkedEmail: string | null): void;
  dispose(): void;
}

const RESULT_LABEL: Record<string, string> = { win: "G", draw: "E", loss: "P" };

function reducedMotion(): boolean {
  return (
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/**
 * Numero que sube contando. Sin esto la pantalla es una tabla; con esto, un premio.
 *
 * El adorno no puede costar la verdad: `requestAnimationFrame` deja de dispararse
 * cuando la pestana pierde el foco, y sin red de seguridad el contador se queda
 * congelado a medias PARA SIEMPRE — el jugador vuelve y ve 4 partidos donde
 * tiene 27. Por eso hay un plazo que escribe el valor final pase lo que pase, y
 * la cuenta se cierra tambien al volver a la pestana.
 */
function countUp(el: HTMLElement, to: number, delayMs: number): () => void {
  if (to === 0 || reducedMotion()) {
    el.textContent = String(to);
    return () => {};
  }
  const duration = Math.min(900, 260 + to * 12);
  let raf = 0;
  let startedAt = 0;
  let done = false;
  el.textContent = "0";

  const settle = (): void => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    el.textContent = String(to);
    document.removeEventListener("visibilitychange", onVisible);
  };

  function onVisible(): void {
    if (!document.hidden) settle();
  }

  const step = (now: number): void => {
    if (done) return;
    if (startedAt === 0) startedAt = now;
    const k = Math.min(1, (now - startedAt) / duration);
    if (k >= 1) {
      settle();
      return;
    }
    // easeOutCubic: arranca rapido y se posa, en vez de frenar de golpe.
    el.textContent = String(Math.round(to * (1 - (1 - k) ** 3)));
    raf = requestAnimationFrame(step);
  };

  const timer = window.setTimeout(() => {
    raf = requestAnimationFrame(step);
  }, delayMs);
  // Los plazos siguen corriendo con la pestana de fondo aunque se acoten; los
  // frames no. Este es el que garantiza que el numero acaba siendo el correcto.
  const guard = window.setTimeout(settle, delayMs + duration + 80);
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    clearTimeout(timer);
    clearTimeout(guard);
    settle();
  };
}

// ------------------------------------------------------------------ hexagono

/** Punto `i` del hexagono a radio `r`. El eje 0 apunta arriba. */
function hexPoint(i: number, r: number): [number, number] {
  const angle = ((-90 + i * 60) * Math.PI) / 180;
  return [100 + r * Math.cos(angle), 100 + r * Math.sin(angle)];
}

function hexPath(radii: readonly number[]): string {
  return radii
    .map((r, i) => {
      const [x, y] = hexPoint(i, r);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/**
 * El hexagono de atributos.
 *
 * Es SVG en el DOM y no un canvas: escala sin pixelarse en cualquier pantalla,
 * hereda los colores del tema y se puede animar con CSS sin escribir un bucle.
 *
 * El radio NO es proporcional a la nota, sino a la nota por encima del suelo de
 * la escala: como nadie baja de 40, dibujarlo desde cero daria seis hexagonos
 * casi iguales y el grafico no diria nada.
 */
function radarSvg(rating: PlayerRating): string {
  const R = 76;
  const ring = (k: number): string =>
    `<polygon class="radar-ring" points="${hexPath(Array.from({ length: 6 }, () => R * k))}" />`;

  const radii = rating.attributes.map((a) => {
    const norm = Math.max(0, Math.min(1, (a.value - 40) / 59));
    // Un suelo visible: un atributo a 40 tiene que verse como un punto, no como
    // el centro exacto, o la figura degenera en una estrella rota.
    return R * (0.16 + 0.84 * norm);
  });

  const labels = rating.attributes
    .map((a, i) => {
      const [x, y] = hexPoint(i, R + 20);
      return `<text class="radar-label" x="${x.toFixed(1)}" y="${y.toFixed(1)}">${a.label}</text>
              <text class="radar-value" x="${x.toFixed(1)}" y="${(y + 13).toFixed(1)}">${a.value}</text>`;
    })
    .join("");

  const spokes = Array.from({ length: 6 }, (_, i) => {
    const [x, y] = hexPoint(i, R);
    return `<line class="radar-spoke" x1="100" y1="100" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" />`;
  }).join("");

  return `
    <svg class="radar" viewBox="0 0 200 200" role="img"
         aria-label="${t("Atributos")}: ${rating.attributes.map((a) => `${a.label} ${a.value}`).join(", ")}">
      ${ring(1)}${ring(0.72)}${ring(0.44)}
      ${spokes}
      <polygon class="radar-shape" points="${hexPath(radii)}" />
      ${rating.attributes
        .map((_a, i) => {
          const [x, y] = hexPoint(i, radii[i] ?? 0);
          return `<circle class="radar-dot" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.2" />`;
        })
        .join("")}
      ${labels}
    </svg>`;
}

// ------------------------------------------------------------------- bloques

function attributeBarsHtml(rating: PlayerRating): string {
  return rating.attributes
    .map(
      (a, i) => `
      <div class="attr-row" style="--i: ${i}" title="${a.hint}">
        <span class="attr-name">${a.label}</span>
        <span class="attr-bar"><span style="width: ${Math.round(((a.value - 40) / 59) * 100)}%"></span></span>
        <span class="attr-num">${a.value}</span>
      </div>`,
    )
    .join("");
}

const FORM_WORD: Record<number, string> = {
  [-2]: t("En caída"),
  [-1]: t("Irregular"),
  [0]: t("Estable"),
  [1]: t("En alza"),
  [2]: t("En racha"),
};

function playerCardHtml(profile: Profile, rating: PlayerRating): string {
  const level = levelFor(profile.xp);
  const form = formOf(profile);
  const name = profile.identity.nickname || t("Jugador");
  const last5 = profile.recent
    .slice(0, 5)
    .map((m, i) => `<span class="form-dot is-${m.result}" style="--i: ${i}"></span>`)
    .join("");

  return `
    <section class="pcard" data-player-card>
      <div class="pcard-glow" aria-hidden="true"></div>
      <div class="pcard-top">
        <div class="pcard-rating">
          <span class="pcard-overall" data-count="overall">0</span>
          <span class="pcard-overall-label">${t("MEDIA")}</span>
        </div>
        <div class="pcard-id">
          <span class="pcard-shirt">${profile.identity.shirtNumber}</span>
          <h2 class="pcard-name">${escapeHtml(name)}</h2>
          <span class="pcard-arch">${rating.archetype} <em>· ${rating.archetypeHint}</em></span>
          <span class="pcard-division">${level.division} · ${t("NIVEL {n}", { n: level.level })}</span>
        </div>
      </div>

      <div class="pcard-xp">
        <span class="pcard-xp-bar"><span style="width: ${Math.round(level.progress * 100)}%"></span></span>
        <span class="pcard-xp-text">${t("{a} / {b} XP para el nivel {n}", {
          a: level.into,
          b: level.need,
          n: level.level + 1,
        })}</span>
      </div>

      <div class="pcard-body">
        <div class="pcard-radar">${radarSvg(rating)}</div>
        <div class="pcard-attrs">${attributeBarsHtml(rating)}</div>
      </div>

      <div class="pcard-foot">
        <div class="pcard-form">
          <span class="pcard-foot-label">${t("Forma")}</span>
          <span class="pcard-form-dots">${last5 || `<em>${t("sin datos")}</em>`}</span>
          <span class="pcard-form-word is-f${form}">${FORM_WORD[form] ?? ""}</span>
        </div>
        ${
          rating.provisional > 0
            ? `<p class="pcard-provisional">${t(
                "Media provisional: te faltan {n} partidos para que sea del todo tuya.",
                { n: rating.provisional },
              )}</p>`
            : `<p class="pcard-provisional">${t("Tu punto débil ahora mismo: {a}.", {
                a: rating.worst.label,
              })}</p>`
        }
      </div>
    </section>`;
}

function tileHtml(label: string, key: string, i: number, hint = ""): string {
  return `
    <div class="career-tile" style="--i: ${i}">
      <span class="career-tile-value" data-count="${key}">0</span>
      <span class="career-tile-label">${label}</span>
      ${hint ? `<span class="career-tile-hint">${hint}</span>` : ""}
    </div>`;
}

// -------------------------------------------------------------------- fechas

function dateLabel(at: number): string {
  const d = new Date(at);
  try {
    return d.toLocaleDateString(lang() === "es" ? "es-ES" : "en-GB", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return `${d.getDate()}/${d.getMonth() + 1}`;
  }
}

function clock(seconds: number): string {
  const m = Math.floor(Math.max(0, seconds) / 60);
  const s = Math.round(Math.max(0, seconds) % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Tiempo largo, el de toda la carrera.
 *
 * `clock()` sirve para un partido; para una carrera escribia "240:00", que es
 * correcto y no lo lee nadie. Cuatro horas se dicen en horas.
 */
function longClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) return t("{m} min", { m });
  return t("{h} h {m} min", { h, m });
}

// ----------------------------------------------------------------- historial

/**
 * Un partido del historial.
 *
 * Se usa `<details>` y no un desplegable a mano por una razon practica: el
 * navegador ya sabe abrirlo con teclado, anunciarlo a un lector de pantalla y
 * dejarlo cerrado por defecto. Escribirlo con JavaScript seria mas codigo para
 * hacerlo peor.
 */
function historyRowHtml(m: MatchRecord, i: number): string {
  const cell = (label: string, value: string | number): string =>
    `<div class="hist-cell"><span>${label}</span><strong>${value}</strong></div>`;
  const passPct = m.passes > 0 ? Math.round((m.passesOk / m.passes) * 100) : 0;
  const shotPct = m.shots > 0 ? Math.round((m.goals / m.shots) * 100) : 0;

  return `
    <details class="hist-item is-${m.result}" style="--i: ${i}">
      <summary class="hist-head">
        <span class="hist-when">${dateLabel(m.at)}</span>
        <span class="hist-mode">${escapeHtml(m.modeId.toUpperCase())}${
          m.online ? `<em class="hist-online">${t("ONLINE")}</em>` : ""
        }</span>
        <span class="hist-score">${m.goalsFor}<i>–</i>${m.goalsAgainst}</span>
        <span class="hist-line">
          ${m.goals > 0 ? `<b>${m.goals}</b>${t("G")}` : ""}
          ${m.assists > 0 ? `<b>${m.assists}</b>${t("A")}` : ""}
          ${m.mvp ? `<em class="hist-mvp">MVP</em>` : ""}
        </span>
        <span class="hist-arrow" aria-hidden="true">›</span>
      </summary>
      <div class="hist-body">
        ${cell(t("Puntuación"), m.rating)}
        ${cell(t("Tiros"), m.shots)}
        ${cell(t("Efectividad"), `${shotPct}%`)}
        ${cell(t("Pases"), `${m.passesOk}/${m.passes}`)}
        ${cell(t("Acierto"), `${passPct}%`)}
        ${cell(t("Recuperaciones"), m.recoveries)}
        ${cell(t("Pérdidas"), m.turnovers)}
        ${cell(t("Duración"), clock(m.seconds))}
        ${m.xp > 0 ? cell("XP", `+${m.xp}`) : ""}
        ${m.overall > 0 ? cell(t("Media"), m.overall) : ""}
      </div>
    </details>`;
}

function historyHtml(profile: Profile): string {
  if (profile.recent.length === 0) {
    return `<p class="career-empty">${t("Aquí aparecerá cada partido que juegues, con tu ficha entera. Todavía no hay ninguno.")}</p>`;
  }
  return profile.recent.map((m, i) => historyRowHtml(m, i)).join("");
}

// -------------------------------------------------------------------- logros

function badgeCardHtml(bp: BadgeProgress, i: number): string {
  const tierName = TIER_NAMES[bp.tier] ?? "";
  const goal = bp.next ?? bp.badge.tiers[bp.badge.tiers.length - 1] ?? 0;
  return `
    <div class="bdg is-t${bp.tier}" style="--i: ${i}" title="${bp.badge.hint}">
      <span class="bdg-icon">${bp.badge.icon}</span>
      <div class="bdg-body">
        <div class="bdg-head">
          <strong>${bp.badge.label}</strong>
          <span class="bdg-tier">${tierName}</span>
        </div>
        <span class="bdg-bar"><span style="width: ${Math.round(bp.progress * 100)}%"></span></span>
        <span class="bdg-meta">${
          bp.next === null
            ? t("{v} · al máximo", { v: bp.value })
            : t("{v} / {n}", { v: bp.value, n: goal })
        }</span>
      </div>
      <span class="bdg-pips" aria-hidden="true">${[0, 1, 2, 3]
        .map((k) => `<i class="${k < bp.tier ? "is-on" : ""}"></i>`)
        .join("")}</span>
    </div>`;
}

const RARITY_ORDER: Record<Rarity, number> = { common: 0, rare: 1, epic: 2, legendary: 3 };

function trophyHtml(tr: Trophy, unlockedAt: number | undefined, i: number): string {
  const done = Boolean(unlockedAt);
  return `
    <div class="trf is-${tr.rarity}${done ? " is-done" : ""}" style="--i: ${i}" title="${tr.hint}">
      <span class="trf-icon">${done ? tr.icon : "🔒"}</span>
      <span class="trf-text">
        <strong>${tr.label}</strong>
        <span>${done ? tr.hint : tr.hint}</span>
      </span>
      <span class="trf-rarity">${RARITY_NAMES[tr.rarity]}</span>
    </div>`;
}

function trophiesHtml(profile: Profile): string {
  // Primero lo conseguido y, dentro, lo mas raro: la pantalla tiene que abrir
  // por lo que presumir, no por lo que falta.
  const ordered = [...TROPHIES].sort((a, b) => {
    const aDone = profile.unlocked[a.id] ? 0 : 1;
    const bDone = profile.unlocked[b.id] ? 0 : 1;
    if (aDone !== bDone) return aDone - bDone;
    const rare = RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity];
    return aDone === 0 ? rare : -rare;
  });
  return ordered.map((tr, i) => trophyHtml(tr, profile.unlocked[tr.id], i)).join("");
}

// ------------------------------------------------------------------- records

/** Curva de la media a lo largo de la carrera. Solo con partidos que la traen. */
function overallSparkline(profile: Profile): string {
  const points = profile.recent
    .filter((m) => m.overall > 0)
    .slice(0, 24)
    .reverse()
    .map((m) => m.overall);
  if (points.length < 3) return "";

  const min = Math.min(...points) - 2;
  const max = Math.max(...points) + 2;
  const span = Math.max(1, max - min);
  const step = 300 / Math.max(1, points.length - 1);
  const path = points
    .map((v, i) => `${(i * step).toFixed(1)},${(60 - ((v - min) / span) * 56).toFixed(1)}`)
    .join(" ");
  const first = points[0] ?? 0;
  const last = points[points.length - 1] ?? 0;
  const delta = last - first;

  return `
    <div class="spark-wrap">
      <svg class="spark" viewBox="0 0 300 64" preserveAspectRatio="none" aria-hidden="true">
        <polyline class="spark-line" points="${path}" />
      </svg>
      <span class="spark-meta">${t("Últimos {n} partidos", { n: points.length })} ·
        <b class="${delta >= 0 ? "is-up" : "is-down"}">${delta >= 0 ? "+" : ""}${delta}</b></span>
    </div>`;
}

function recordsHtml(profile: Profile): string {
  const r = profile.records;
  const tot = profile.totals;
  const item = (label: string, value: string | number, note = ""): string => `
    <div class="rec">
      <span class="rec-value">${value}</span>
      <span class="rec-label">${label}</span>
      ${note ? `<span class="rec-note">${note}</span>` : ""}
    </div>`;

  const winPct = tot.matches > 0 ? Math.round((tot.wins / tot.matches) * 100) : 0;
  const perMatch = (n: number): string =>
    tot.matches > 0 ? (n / tot.matches).toFixed(2).replace(".", ",") : "0";

  return `
    ${item(t("Goles en un partido"), r.goalsInMatch)}
    ${item(t("Asistencias en un partido"), r.assistsInMatch)}
    ${item(t("Robos en un partido"), r.recoveriesInMatch)}
    ${item(t("Mejor puntuación"), r.ratingInMatch)}
    ${item(t("Mayor goleada"), r.biggestWin > 0 ? `+${r.biggestWin}` : "—")}
    ${item(t("Gol más rápido"), r.fastestGoal === null ? "—" : clock(r.fastestGoal))}
    ${item(t("Mejor racha"), profile.bestWinStreak, t("victorias seguidas"))}
    ${item(t("Mejor semana"), r.bestWeekPoints, t("puntos"))}
    ${item(t("Días seguidos"), Math.max(r.bestDayStreak, profile.dayStreak))}
    ${item(t("Mejor media"), Math.max(r.bestOverall, profile.overall))}
    ${item(t("Victorias"), `${winPct}%`, `${tot.wins}/${tot.matches}`)}
    ${item(t("Goles por partido"), perMatch(tot.goals))}
    ${item(t("Tiempo jugado"), longClock(tot.seconds))}
    ${item(t("Pases completados"), tot.passesOk)}`;
}

function partnersHtml(profile: Profile): string {
  const top = profile.partners.slice(0, 5);
  if (top.length === 0) {
    return `<p class="career-empty">${t("Juega online y aquí saldrá con quién mejor te entiendes.")}</p>`;
  }
  return top
    .map((p, i) => {
      const pct = p.played === 0 ? 0 : Math.round((p.won / p.played) * 100);
      return `
        <div class="career-partner" style="--i: ${i}">
          <span class="career-partner-nick">${escapeHtml(p.nickname)}</span>
          <span class="career-partner-bar"><span style="width: ${pct}%"></span></span>
          <span class="career-partner-meta">${p.won}/${p.played} · ${pct}%</span>
        </div>`;
    })
    .join("");
}

function formHtml(profile: Profile): string {
  if (profile.recent.length === 0) {
    return `<p class="career-empty">${t("Aún no has jugado ningún partido. El primero es el que más pesa.")}</p>`;
  }
  return profile.recent
    .slice(0, 10)
    .map(
      (m, i) => `
        <span class="career-form-pill is-${m.result}" style="--i: ${i}" title="${escapeHtml(m.modeId)} · ${m.goalsFor}-${m.goalsAgainst}${m.mvp ? " · MVP" : ""}">
          ${RESULT_LABEL[m.result] ?? "?"}
        </span>`,
    )
    .join("");
}

/** Cuánto falta para que el resumen semanal personal se reinicie. */
function daysToMonday(now = new Date()): number {
  const day = now.getDay() || 7;
  return day === 1 ? 7 : 8 - day;
}

type TabId = "card" | "history" | "badges" | "records" | "friends";

export function createCareerScreen(root: HTMLElement = document.body): CareerScreenHandle {
  const overlay = document.createElement("div");
  overlay.id = "career-screen";
  overlay.className = "hub-overlay career-overlay is-hidden";
  overlay.innerHTML = `
    <div class="career-bg" aria-hidden="true"></div>
    <div class="menu-shell career-shell">
      <header class="career-topbar">
        <button type="button" class="lobby-back lobby-back--ghost" data-career-back>${t("← Menú")}</button>
        <!-- Igual que la entrada del menu: no es un modo, son tus numeros. -->
        <h1 class="career-title">${t("Mis estadísticas")}</h1>
        <span class="career-streak" data-streak></span>
      </header>

      <!--
        Online y offline, separados: contra personas y contra la IA son dos
        juegos, y una media hecha a base de golear bots no dice nada de como
        juegas contra gente.
      -->
      <div class="career-sides" role="tablist" aria-label="${t("Qué estadísticas")}">
        <button type="button" role="tab" class="career-side is-on" data-side="online">${t("ONLINE")}</button>
        <button type="button" role="tab" class="career-side" data-side="offline">${t("CONTRA LA IA")}</button>
      </div>

      <nav class="career-tabs" role="tablist">
        <button type="button" role="tab" class="career-tab is-on" data-tab="card">${t("FICHA")}</button>
        <button type="button" role="tab" class="career-tab" data-tab="history">${t("HISTORIAL")}</button>
        <button type="button" role="tab" class="career-tab" data-tab="badges">${t("LOGROS")}</button>
        <button type="button" role="tab" class="career-tab" data-tab="records">${t("RÉCORDS")}</button>
        <button type="button" role="tab" class="career-tab" data-tab="friends" data-online-only>${t("AMIGOS")}</button>
      </nav>

      <div class="career-scroll">
        <div class="career-panel is-on" data-panel="card">
          <div data-card-host></div>

          <div class="pcard-share">
            <button type="button" class="lobby-btn-ghost" data-share-card>${t("Compartir mi ficha")}</button>
          </div>

          <section class="career-tiles">
            ${tileHtml(t("Partidos"), "matches", 0)}
            ${tileHtml(t("Goles"), "goals", 1)}
            ${tileHtml(t("Asistencias"), "assists", 2)}
            ${tileHtml("MVP", "mvps", 3)}
          </section>

          <section class="career-card career-week" style="--i: 0">
            <div class="career-card-head">
              <h2>${t("Esta semana")}</h2>
              <span class="career-card-note" data-week-reset></span>
            </div>
            <div class="career-week-body">
              <div class="career-week-points">
                <span data-count="weekPoints">0</span>
                <small>${t("puntos")}</small>
              </div>
              <ul class="career-week-list" data-week-list></ul>
            </div>
          </section>

          <section class="career-card" style="--i: 1">
            <div class="career-card-head">
              <h2>${t("Últimos partidos")}</h2>
              <span class="career-card-note" data-record></span>
            </div>
            <div class="career-form" data-form></div>
          </section>

          <section class="career-card" style="--i: 2">
            <div class="career-card-head">
              <h2>${t("Con quién ganas")}</h2>
            </div>
            <div class="career-partners" data-partners></div>
          </section>

          <section class="career-card career-account" style="--i: 3" data-account hidden>
            <div class="career-card-head">
              <h2>${t("Tu cuenta")}</h2>
            </div>
            <!--
              Una sola frase, no tres trozos. Estaba partida en dos llamadas a
              la traduccion mas una cola en español a pelo, y en ingles se leia
              "your career . Conecta Google si...". Una frase con formato dentro
              solo se puede traducir entera, negritas incluidas.
            -->
            <p class="career-account-hint">
              ${t("Juegas como invitado y tu carrera <strong>ya se guarda sola</strong>. Conecta Google si quieres recuperarla en otro móvil u otro ordenador.")}
            </p>
            <div class="career-google" data-google hidden>
              <div class="career-google-btn" data-google-btn></div>
              <p class="career-google-linked" data-google-linked hidden></p>
            </div>
            <p class="career-account-sep" data-account-sep hidden>${t("o con un código, sin cuentas")}</p>
            <div class="career-account-actions">
              <button type="button" class="lobby-btn-ghost" data-account-code>${t("Sacar mi código")}</button>
              <form class="career-account-redeem" data-redeem-form>
                <input
                  type="text"
                  class="nick-input career-account-input"
                  data-redeem-input
                  placeholder="${t("pegar código")}"
                  maxlength="20"
                  autocomplete="off"
                  spellcheck="false"
                />
                <button type="submit" class="lobby-btn-ghost">${t("Usar")}</button>
              </form>
            </div>
            <output class="career-account-out" data-account-out hidden></output>
          </section>
        </div>

        <div class="career-panel" data-panel="history">
          <div class="career-card-head career-panel-head">
            <h2>${t("Historial")}</h2>
            <span class="career-card-note" data-history-note></span>
          </div>
          <div class="hist-list" data-history></div>
          <p class="hist-foot">${t("Se guardan tus últimos 25 partidos.")}</p>
        </div>

        <div class="career-panel" data-panel="badges">
          <div class="career-card-head career-panel-head">
            <h2>${t("Insignias")}</h2>
            <span class="career-card-note" data-badge-count></span>
          </div>
          <div class="bdg-grid" data-badges></div>

          <div class="career-card-head career-panel-head">
            <h2>${t("Trofeos")}</h2>
            <span class="career-card-note" data-trophy-count></span>
          </div>
          <div class="trf-grid" data-trophies></div>
        </div>

        <div class="career-panel" data-panel="records">
          <div class="career-card-head career-panel-head">
            <h2>${t("Tu mejor versión")}</h2>
          </div>
          <div class="rec-grid" data-records></div>

          <section class="career-card" style="--i: 0">
            <div class="career-card-head">
              <h2>${t("Cómo ha ido tu media")}</h2>
            </div>
            <div data-spark></div>
          </section>
        </div>

        <!--
          AMIGOS: la semana entre vosotros. Es lo que convierte tus numeros en
          algo que mirar cada dia: no "tengo 40 puntos", sino "Ana me pasa por
          doce". Cada fila abre su ficha comparada con la tuya.
        -->
        <div class="career-panel" data-panel="friends">
          <div class="career-card-head career-panel-head">
            <h2>${t("La semana entre amigos")}</h2>
            <button type="button" class="lobby-btn-ghost career-board-refresh" data-board-refresh aria-label="${t("Actualizar")}">↻</button>
          </div>
          <div class="career-board" data-board></div>
          <p class="career-empty career-board-hint">${t("Pulsa en un amigo para ver su ficha comparada con la tuya.")}</p>
        </div>
      </div>
    </div>
  `;
  root.appendChild(overlay);

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;

  const back = q<HTMLButtonElement>("[data-career-back]");
  const streak = q("[data-streak]");
  const cardHost = q("[data-card-host]");
  const shareBtn = q<HTMLButtonElement>("[data-share-card]");
  const weekReset = q("[data-week-reset]");
  const weekList = q("[data-week-list]");
  const record = q("[data-record]");
  const form = q("[data-form]");
  const partners = q("[data-partners]");
  const history = q("[data-history]");
  const historyNote = q("[data-history-note]");
  const badges = q("[data-badges]");
  const badgeCount = q("[data-badge-count]");
  const trophies = q("[data-trophies]");
  const trophyCount = q("[data-trophy-count]");
  const records = q("[data-records]");
  const spark = q("[data-spark]");
  const accountCard = q("[data-account]");
  const codeBtn = q<HTMLButtonElement>("[data-account-code]");
  const redeemForm = q<HTMLFormElement>("[data-redeem-form]");
  const redeemInput = q<HTMLInputElement>("[data-redeem-input]");
  const accountOut = q("[data-account-out]");

  const googleWrap = q("[data-google]");
  const googleBtn = q("[data-google-btn]");
  const googleLinked = q("[data-google-linked]");
  const accountSep = q("[data-account-sep]");

  const scroll = q(".career-scroll");
  const tabs = [...overlay.querySelectorAll<HTMLButtonElement>("[data-tab]")];
  const panels = [...overlay.querySelectorAll<HTMLElement>("[data-panel]")];

  let account: AccountBinding | null = null;
  let googleMounted = false;
  let shown: Profile | null = null;
  let cardHandle: { dispose(): void } | null = null;

  const showTab = (id: TabId): void => {
    for (const tab of tabs) tab.classList.toggle("is-on", tab.dataset.tab === id);
    for (const panel of panels) panel.classList.toggle("is-on", panel.dataset.panel === id);
    // Cada pestana empieza por arriba: heredar el scroll de la anterior deja al
    // jugador mirando el medio de una lista que no ha abierto.
    scroll.scrollTop = 0;
  };

  for (const tab of tabs) {
    tab.addEventListener("click", () => {
      const id = (tab.dataset.tab as TabId) ?? "card";
      showTab(id);
      if (id === "friends") void loadBoard();
    });
  }

  const say = (text: string, tone: "ok" | "bad" | "code"): void => {
    accountOut.hidden = false;
    accountOut.textContent = text;
    accountOut.dataset.tone = tone;
  };

  codeBtn.addEventListener("click", () => {
    if (!account) return;
    codeBtn.disabled = true;
    account.requestCode();
  });

  redeemForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const code = redeemInput.value.trim();
    if (!account || !code) return;
    account.redeem(code);
  });

  shareBtn.addEventListener("click", () => {
    if (!shown) return;
    // La tarjeta se monta bajo demanda: dibujar un PNG de 1200x630 al abrir la
    // pantalla costaria ese trabajo a todo el mundo, y casi nadie comparte.
    cardHandle?.dispose();
    const slot = document.createElement("div");
    slot.className = "pcard-share-slot";
    shareBtn.after(slot);
    cardHandle = mountPlayerCard(slot, shown);
    shareBtn.hidden = true;
  });

  let resolveClose: (() => void) | null = null;
  let cancelCounts: Array<() => void> = [];

  const stopCounts = (): void => {
    for (const stop of cancelCounts) stop();
    cancelCounts = [];
  };

  const hide = (): void => {
    stopCounts();
    overlay.classList.add("is-hidden");
    overlay.classList.remove("is-open");
  };

  const close = (): void => {
    const done = resolveClose;
    resolveClose = null;
    hide();
    done?.();
  };

  back.addEventListener("click", close);

  const onKey = (event: KeyboardEvent): void => {
    if (overlay.classList.contains("is-hidden")) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };
  window.addEventListener("keydown", onKey);

  let shownPair: ProfilePair | null = null;
  let shownSide: ProfileSide = "online";
  const showSide = (side: ProfileSide): void => {
    shownSide = side;
    for (const btn of overlay.querySelectorAll<HTMLElement>("[data-side]")) {
      btn.classList.toggle("is-on", btn.dataset.side === side);
      btn.setAttribute("aria-selected", String(btn.dataset.side === side));
    }
    // Los amigos son del lado online: contra la IA no hay con quien compararse.
    const friendsTab = overlay.querySelector<HTMLElement>("[data-tab='friends']");
    if (friendsTab) friendsTab.hidden = side !== "online" || !social;
    if (side !== "online" && friendsTab?.classList.contains("is-on")) showTab("card");
    if (shownPair) render(shownPair[side]);
  };

  // --- amigos -------------------------------------------------------------
  let social: CareerSocial | null = null;
  const boardEl = q("[data-board]");
  const loadBoard = async (): Promise<void> => {
    if (!social) return;
    boardEl.innerHTML = `<p class="career-empty">${t("Cargando…")}</p>`;
    const rows = await social.board();
    if (!rows) {
      boardEl.innerHTML = `<p class="career-empty">${t("No se ha podido cargar. Comprueba la conexión.")}</p>`;
      return;
    }
    if (rows.length <= 1) {
      boardEl.innerHTML = `<p class="career-empty">${t("Aún no tienes amigos. Añádelos en Multijugador con su código, o pulsando su nombre en el chat.")}</p>`;
      return;
    }
    boardEl.innerHTML = `
      <table class="board-table">
        <thead><tr>
          <th>#</th><th>${t("Jugador")}</th><th>${t("PTS")}</th><th>${t("Media")}</th><th>${t("PJ")}</th><th>${t("% V")}</th>
        </tr></thead>
        <tbody>${rows
          .map(
            (r, i) => `
          <tr class="board-row${r.me ? " is-me" : ""}" ${r.me ? "" : `data-friend="${escapeHtml(r.clientId)}" data-nick="${escapeHtml(r.nickname)}" tabindex="0"`}>
            <td class="board-pos">${i === 0 && r.weekPoints > 0 ? "👑" : i + 1}</td>
            <td class="board-name">${escapeHtml(r.nickname)}${r.me ? ` <em>${t("TÚ")}</em>` : ""}</td>
            <td class="board-pts">${r.weekPoints}</td>
            <td>${r.overall || "—"}</td>
            <td>${r.matches}</td>
            <td>${r.matches > 0 ? `${Math.round((r.wins / r.matches) * 100)}%` : "—"}</td>
          </tr>`,
          )
          .join("")}</tbody>
      </table>`;
  };
  boardEl.addEventListener("click", (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>("[data-friend]");
    if (row && social) social.open(row.dataset.friend!, row.dataset.nick ?? "");
  });
  boardEl.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const row = (event.target as HTMLElement).closest<HTMLElement>("[data-friend]");
    if (row && social) social.open(row.dataset.friend!, row.dataset.nick ?? "");
  });
  q("[data-board-refresh]").addEventListener("click", () => void loadBoard());
  for (const btn of overlay.querySelectorAll<HTMLElement>("[data-side]")) {
    btn.addEventListener("click", () => showSide(btn.dataset.side as ProfileSide));
  }

  const render = (profile: Profile): void => {
    stopCounts();
    shown = profile;
    cardHandle?.dispose();
    cardHandle = null;
    shareBtn.hidden = false;
    overlay.querySelector(".pcard-share-slot")?.remove();

    const tot = profile.totals;
    const rating = ratePlayer(tot);

    streak.innerHTML =
      profile.dayStreak > 0
        ? `<span class="career-streak-flame">🔥</span> ${
            profile.dayStreak === 1
              ? t("1 día seguido")
              : t("{n} días seguidos", { n: profile.dayStreak })
          }`
        : "";

    cardHost.innerHTML = playerCardHtml(profile, rating);

    const values: Record<string, number> = {
      overall: profile.overall,
      matches: tot.matches,
      goals: tot.goals,
      assists: tot.assists,
      mvps: tot.mvps,
      weekPoints: profile.weekly.points,
    };
    overlay.querySelectorAll<HTMLElement>("[data-count]").forEach((el, i) => {
      const key = el.dataset.count ?? "";
      cancelCounts.push(countUp(el, values[key] ?? 0, 120 + i * 70));
    });

    const days = daysToMonday();
    weekReset.textContent =
      days === 1 ? t("se reinicia en 1 día") : t("se reinicia en {n} días", { n: days });
    weekList.innerHTML = `
      <li><span>${t("Partidos")}</span><strong>${profile.weekly.matches}</strong></li>
      <li><span>${t("Goles")}</span><strong>${profile.weekly.goals}</strong></li>
      <li><span>${t("Asistencias")}</span><strong>${profile.weekly.assists}</strong></li>
      <li><span>MVP</span><strong>${profile.weekly.mvps}</strong></li>
    `;

    /*
     * El registro va en una sola clave, no letra a letra. G/E/P son Ganados,
     * Empatados y Perdidos, pero en la tabla del marcador G y P son Goles y
     * Pases: partirlo en `t("G")` haria que las dos pantallas se pelearan por
     * la misma entrada del diccionario.
     */
    record.textContent = `${t("{g}G · {e}E · {p}P", {
      g: tot.wins,
      e: tot.draws,
      p: tot.losses,
    })}${profile.bestWinStreak > 1 ? ` · ${t("mejor racha {n}", { n: profile.bestWinStreak })}` : ""}`;
    form.innerHTML = formHtml(profile);
    partners.innerHTML = partnersHtml(profile);

    history.innerHTML = historyHtml(profile);
    historyNote.textContent =
      profile.recent.length > 0 ? t("{n} partidos", { n: profile.recent.length }) : "";

    const progress = allBadgeProgress(profile);
    // Lo que esta a punto de subir, primero: es lo que hace volver al campo.
    const ordered = [...progress].sort((a, b) => {
      if (a.next === null !== (b.next === null)) return a.next === null ? 1 : -1;
      return b.progress - a.progress;
    });
    badges.innerHTML = ordered.map((bp, i) => badgeCardHtml(bp, i)).join("");
    const tierTotal = badgeTierTotal(profile);
    badgeCount.textContent = `${tierTotal.done}/${tierTotal.max}`;

    trophies.innerHTML = trophiesHtml(profile);
    const doneTrophies = TROPHIES.filter((tr) => profile.unlocked[tr.id]).length;
    trophyCount.textContent = `${doneTrophies}/${TROPHIES.length}`;

    records.innerHTML = recordsHtml(profile);
    spark.innerHTML =
      overallSparkline(profile) ||
      `<p class="career-empty">${t("Juega unos cuantos partidos y aquí se dibujará tu curva.")}</p>`;
  };

  return {
    bindAccount(binding: AccountBinding): void {
      account = binding;
      accountCard.hidden = false;
    },

    setGoogle(clientId: string | null, linkedEmail: string | null): void {
      if (linkedEmail) {
        googleLinked.hidden = false;
        googleLinked.textContent = t("Conectado como {email}", { email: linkedEmail });
        googleWrap.hidden = false;
      }
      if (!clientId || googleMounted) return;
      googleMounted = true;
      googleWrap.hidden = false;
      accountSep.hidden = false;
      void mountGoogleButton({
        clientId,
        parent: googleBtn,
        onCredential: (credential) => account?.google(credential),
        // Si Google no carga (bloqueador, sin red, o un pais donde no llega),
        // se quita el hueco y queda el codigo. Nunca un boton muerto.
        onUnavailable: () => {
          googleWrap.hidden = true;
          accountSep.hidden = true;
        },
      });
    },

    showCode(code: string): void {
      codeBtn.disabled = false;
      // Se enseña el codigo tal cual, con guiones, para poder copiarlo a mano.
      say(t("Apunta esto: {code}", { code }), "code");
    },

    showRedeemResult(ok: boolean, message: string): void {
      if (ok) redeemInput.value = "";
      say(message, ok ? "ok" : "bad");
    },

    setSocial(next: CareerSocial): void {
      social = next;
      showSide(shownSide);
    },

    show(profiles: ProfilePair): Promise<void> {
      shownPair = profiles;
      // Se abre por el lado donde mas has jugado.
      showSide(profiles.online.totals.matches >= profiles.offline.totals.matches ? "online" : "offline");
      showTab("card");
      overlay.classList.remove("is-hidden");
      overlay.scrollTop = 0;
      // Reinicia las animaciones de entrada tambien al reabrir: quitar la clase,
      // forzar un reflujo y volver a ponerla es lo unico que las rearranca.
      overlay.classList.remove("is-open");
      void overlay.offsetWidth;
      overlay.classList.add("is-open");
      back.focus({ preventScroll: true });
      return new Promise((resolve) => {
        resolveClose = resolve;
      });
    },
    hide,
    close,
    dispose() {
      stopCounts();
      cardHandle?.dispose();
      window.removeEventListener("keydown", onKey);
      overlay.remove();
    },
  };
}

/**
 * Lo que la carrera se lleva del partido, para la pantalla de resultado.
 *
 * Se enseña ahi y no en un aviso suelto: el jugador ya esta mirando esa
 * pantalla, y el premio cae junto al marcador que lo ha provocado. Es la unica
 * forma de que el partido y la carrera se sientan la misma cosa — sin esto, las
 * estadisticas son una pantalla aparte a la que nadie entra.
 */
export function careerRewardHtml(applied: ApplyResult, profile: Profile): string {
  const level = levelFor(profile.xp);
  const gained = applied.overallAfter - applied.overallBefore;
  const levelUp = applied.levelAfter > applied.levelBefore;

  const head = `
    <div class="rew-head">
      <div class="rew-overall">
        <span class="rew-overall-num">${applied.overallAfter}</span>
        <span class="rew-overall-label">${t("MEDIA")}</span>
        ${gained !== 0 ? `<span class="rew-delta ${gained > 0 ? "is-up" : "is-down"}">${gained > 0 ? "+" : ""}${gained}</span>` : ""}
      </div>
      <div class="rew-xp">
        <span class="rew-xp-top">
          <strong>${levelUp ? t("¡NIVEL {n}!", { n: applied.levelAfter }) : t("NIVEL {n}", { n: applied.levelAfter })}</strong>
          <em>+${applied.xpGained} XP</em>
        </span>
        <span class="rew-xp-bar"><span style="width: ${Math.round(level.progress * 100)}%"></span></span>
        <span class="rew-xp-foot">${level.division} · ${t("{a}/{b} para el nivel {n}", {
          a: level.into,
          b: level.need,
          n: level.level + 1,
        })}</span>
      </div>
    </div>`;

  const promos = applied.promoted
    .map(
      (p, i) => `
      <li class="unlock-item is-badge is-t${p.to}" style="--i: ${i}">
        <span class="unlock-icon">${p.badge.icon}</span>
        <span class="unlock-text">
          <strong>${p.badge.label} · ${TIER_NAMES[p.to] ?? ""}</strong>
          <span>${p.badge.hint}</span>
        </span>
      </li>`,
    )
    .join("");

  const trophyItems = applied.unlocked
    .map(
      (tr, i) => `
      <li class="unlock-item is-${tr.rarity}" style="--i: ${applied.promoted.length + i}">
        <span class="unlock-icon">${tr.icon}</span>
        <span class="unlock-text">
          <strong>${tr.label}</strong>
          <span>${tr.hint} · ${RARITY_NAMES[tr.rarity]}</span>
        </span>
      </li>`,
    )
    .join("");

  const recordLine =
    applied.brokenRecords.length > 0
      ? `<p class="rew-record">${t("¡Récord personal!")} ${applied.brokenRecords.length > 1 ? t("({n} batidos)", { n: applied.brokenRecords.length }) : ""}</p>`
      : "";

  const list =
    promos || trophyItems
      ? `<p class="unlock-head">${t("Lo que te llevas")}</p><ul class="unlock-list">${promos}${trophyItems}</ul>`
      : "";

  return `<div class="unlock-block">${head}${recordLine}${list}</div>`;
}
